import React, { useEffect, useState } from "react";
import { Select } from "antd";
import { IconQuestKeyboardArrowDown } from "assets/svg";
import {
  QUEST_ACTION_OPTIONS,
  QUEST_LOGIN_CONDITION_OPTIONS,
  QUEST_TARGET_ROLE_OPTIONS,
} from "common/constants";
import { Controller, useForm } from "react-hook-form";
import type {
  AdminQuest,
  CreateQuestPayload,
  QuestTypeValue,
  SelectOption,
} from "common/types/questManagement.type";
import SectionTitle from "./SectionTitle";
import {
  FieldError,
  FieldLabel,
  inputClassName,
  MonthField,
  QuestFormField,
  selectClassName,
} from "./FormControls";
import UnsavedChangesModal from "./UnsavedChangesModal";
import {
  getQuestFormDefaultValues,
  monthEndDate,
  monthStartDate,
} from "./questFormUtils";
import type { QuestFormValues } from "./questFormUtils";

interface CreateQuestDrawerProps {
  isOpen: boolean;
  onCancel: () => void;
  mode: "create" | "edit";
  initialQuest: AdminQuest | null;
  badgeOptions: SelectOption[];
  isBadgeLoading: boolean;
  badgeErrorMessage: string;
  isDefaultHiddenQuest: boolean;
  isCreating: boolean;
  createErrorMessage: string;
  questType: QuestTypeValue;
  codeSuggestion: string;
  onCreate: (payload: CreateQuestPayload) => Promise<void>;
}

const CreateQuestDrawer = ({
  isOpen,
  onCancel,
  mode,
  initialQuest,
  badgeOptions,
  isBadgeLoading,
  badgeErrorMessage,
  isDefaultHiddenQuest,
  isCreating,
  createErrorMessage,
  questType,
  codeSuggestion,
  onCreate,
}: CreateQuestDrawerProps) => {
  const [isUnsavedConfirmOpen, setIsUnsavedConfirmOpen] = useState(false);
  const effectiveQuestType =
    (initialQuest?.quest_type as QuestTypeValue | undefined) ?? questType;
  const canConfigureLoginCondition = effectiveQuestType === "hidden";
  const {
    formState: { errors, isDirty },
    control,
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<QuestFormValues>({
    defaultValues: getQuestFormDefaultValues({
      codeSuggestion,
      initialQuest,
      isDefaultHiddenQuest,
    }),
  });

  useEffect(() => {
    reset(
      getQuestFormDefaultValues({
        codeSuggestion,
        initialQuest,
        isDefaultHiddenQuest,
      }),
    );
  }, [codeSuggestion, initialQuest, isDefaultHiddenQuest, isOpen, reset]);

  useEffect(() => {
    if (!isOpen) {
      setIsUnsavedConfirmOpen(false);
    }
  }, [isOpen]);

  const shouldGrantBadge = watch("shouldGrantBadge");
  const rewardBadgeId = watch("reward_badge_id");
  const activeStartMonth = watch("active_start_month");
  const actionType = watch("action_type");
  const conditionType = watch("condition_type");
  const isLoginStreak =
    canConfigureLoginCondition &&
    actionType === "user_login" &&
    conditionType === "streak";

  useEffect(() => {
    if (!canConfigureLoginCondition || actionType !== "user_login") {
      setValue("condition_type", "count");
    }
  }, [actionType, canConfigureLoginCondition, setValue]);

  useEffect(() => {
    if (isLoginStreak) {
      setValue("active_start_month", "");
      setValue("active_end_month", "");
    }
  }, [isLoginStreak, setValue]);

  useEffect(() => {
    if (!shouldGrantBadge) {
      setValue("reward_badge_id", "");
      return;
    }

    if (!rewardBadgeId && badgeOptions[0]?.value) {
      setValue("reward_badge_id", badgeOptions[0].value);
    }
  }, [badgeOptions, rewardBadgeId, setValue, shouldGrantBadge]);

  const submitQuest = async (values: QuestFormValues) => {
    const description = values.description.trim();
    const selectedBadgeId = values.reward_badge_id;

    await onCreate({
      code: initialQuest?.code ?? (codeSuggestion || undefined),
      quest_type: effectiveQuestType,
      title: values.title.trim(),
      description: description || undefined,
      action_type: isDefaultHiddenQuest
        ? (initialQuest?.action_type ?? "transaction_completed")
        : values.action_type,
      condition_type:
        canConfigureLoginCondition && values.action_type === "user_login"
          ? values.condition_type
          : "count",
      target_role: isDefaultHiddenQuest ? "all" : values.target_role,
      active_start_at: isDefaultHiddenQuest
        ? undefined
        : isLoginStreak
          ? null
          : monthStartDate(values.active_start_month),
      active_end_at: isDefaultHiddenQuest
        ? undefined
        : isLoginStreak
          ? null
          : monthEndDate(values.active_end_month),
      target_count: values.target_count ?? 1,
      reward_xp: values.reward_xp ?? 0,
      reward_badge_id:
        values.shouldGrantBadge && selectedBadgeId ? selectedBadgeId : null,
      is_hidden: initialQuest?.is_hidden ?? questType === "hidden",
      is_active: values.is_active,
    });
  };

  const handleRequestCancel = () => {
    if (isCreating) return;

    if (isDirty) {
      setIsUnsavedConfirmOpen(true);
      return;
    }

    onCancel();
  };

  const handleStayOnForm = () => {
    setIsUnsavedConfirmOpen(false);
  };

  const handleConfirmCancel = () => {
    setIsUnsavedConfirmOpen(false);
    onCancel();
  };

  return (
    <div
      className={`absolute inset-x-0 bottom-[90px] top-0 z-[8] flex overflow-hidden transition-opacity duration-300 md:inset-0 md:z-30 ${
        isOpen
          ? "pointer-events-auto opacity-100"
          : "pointer-events-none opacity-0"
      }`}
    >
      <button
        aria-label="close create quest form"
        className="min-w-0 flex-1 bg-white/80"
        onClick={handleRequestCancel}
        type="button"
      />

      <aside
        className={`h-full w-full max-w-[640px] shrink-0 overflow-y-auto border-l border-[#d5d5d5] bg-white px-5 pb-8 pt-5 transition-transform duration-300 ease-out sm:px-[30px] sm:pb-[30px] sm:pt-[30px] md:w-[50%] ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <form noValidate onSubmit={handleSubmit(submitQuest)}>
          <div className="flex h-[45px] items-stretch gap-2.5 border-b border-[#d5d5d5] py-2 sm:hidden">
            <div className="w-1 rounded-sm bg-[#1b4b86]" />
            <h2 className="flex items-center text-lg font-bold leading-[1.6] text-[#1b4b86]">
              {mode === "edit" ? "クエスト編集" : "新規クエスト作成"}
            </h2>
          </div>
          <div className="hidden sm:block">
            <SectionTitle
              title={mode === "edit" ? "クエスト編集" : "新規クエスト作成"}
            />
          </div>

          <div className="mt-[25px] flex flex-col gap-[20px]">
            <QuestFormField label="クエスト名入力" required>
              <input
                className={inputClassName}
                maxLength={50}
                placeholder="例：皆勤賞"
                {...register("title", {
                  required: "クエスト名を入力してください。",
                })}
              />
              <FieldError message={errors.title?.message} />
            </QuestFormField>

            <QuestFormField label="クエストコード">
              <input
                className={`${inputClassName} bg-[#f5f5f5]`}
                placeholder="採番中..."
                readOnly
                {...register("code")}
              />
            </QuestFormField>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-[15px]">
              <QuestFormField label="ターゲット指標選択" required>
                <div className="relative">
                  <select
                    className={`${selectClassName} pr-[50px] disabled:bg-[#f5f5f5] disabled:text-[#adbbcc]`}
                    disabled={isDefaultHiddenQuest}
                    {...register("action_type", {
                      required: "ターゲット指標を選択してください。",
                    })}
                  >
                    {QUEST_ACTION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <IconQuestKeyboardArrowDown className="pointer-events-none absolute right-[15px] top-[15px] h-5 w-5" />
                </div>
                <FieldError message={errors.action_type?.message} />
              </QuestFormField>

              <QuestFormField label="対象" required>
                <div className="relative">
                  <select
                    className={`${selectClassName} pr-[50px] disabled:bg-[#f5f5f5] disabled:text-[#adbbcc]`}
                    disabled={isDefaultHiddenQuest}
                    {...register("target_role", {
                      required: "対象を選択してください。",
                    })}
                  >
                    {QUEST_TARGET_ROLE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <IconQuestKeyboardArrowDown className="pointer-events-none absolute right-[15px] top-[15px] h-5 w-5" />
                </div>
                <FieldError message={errors.target_role?.message} />
              </QuestFormField>

              {!isDefaultHiddenQuest &&
              canConfigureLoginCondition &&
              actionType === "user_login" ? (
                <QuestFormField label="ログイン条件">
                  <Controller
                    control={control}
                    name="condition_type"
                    rules={{ required: "ログイン条件を選択してください。" }}
                    render={({ field }) => (
                      <div className="relative">
                        <select
                          className={`${selectClassName} pr-[50px]`}
                          name={field.name}
                          onBlur={field.onBlur}
                          onChange={(event) =>
                            field.onChange(event.target.value)
                          }
                          ref={field.ref}
                          value={field.value}
                        >
                          {QUEST_LOGIN_CONDITION_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <IconQuestKeyboardArrowDown className="pointer-events-none absolute right-[15px] top-[15px] h-5 w-5" />
                      </div>
                    )}
                  />
                  <FieldError message={errors.condition_type?.message} />
                </QuestFormField>
              ) : null}

              <QuestFormField label="目標達成回数" required>
                <input
                  className={inputClassName}
                  min={1}
                  placeholder="例：1"
                  step={1}
                  type="number"
                  {...register("target_count", {
                    required: "目標達成回数を入力してください。",
                    min: {
                      value: 1,
                      message: "目標達成回数は1以上で入力してください。",
                    },
                    valueAsNumber: true,
                  })}
                />
                <FieldError message={errors.target_count?.message} />
              </QuestFormField>
            </div>

            <div className="border-t border-[#d5d5d5] pt-[20px]">
              <h3 className="text-[20px] font-bold leading-[1.6] text-black">
                報酬設定
              </h3>
            </div>

            <QuestFormField label="付与XP" required>
              <div className="flex h-[50px] overflow-hidden rounded-[6px] border border-[#d5d5d5] bg-white">
                <input
                  className="h-full min-w-0 flex-1 px-[15px] text-[16px] leading-[1.6] text-black outline-none placeholder:text-[#adbbcc]"
                  max={10000}
                  min={1}
                  placeholder="100"
                  step={1}
                  type="number"
                  {...register("reward_xp", {
                    required: "付与XPを入力してください。",
                    min: {
                      value: 1,
                      message: "付与XPは1以上で入力してください。",
                    },
                    max: {
                      value: 10000,
                      message: "付与XPは10000以下で入力してください。",
                    },
                    valueAsNumber: true,
                  })}
                />
                <div className="flex w-[45px] items-center justify-center text-[12px] leading-[1.6] text-black">
                  XP
                </div>
              </div>
              <FieldError message={errors.reward_xp?.message} />
            </QuestFormField>

            <label className="flex h-[30px] items-center gap-2.5 text-base leading-[1.6] text-black">
              <input
                className="h-[30px] w-[30px] accent-[#1b4b86] sm:h-[18px] sm:w-[18px]"
                type="checkbox"
                {...register("shouldGrantBadge")}
              />
              バッジを付与する
            </label>

            {shouldGrantBadge ? (
              <div className="flex flex-col gap-[8px]">
                <QuestFormField label="バッジタイプの選択" required>
                  <Controller
                    control={control}
                    name="reward_badge_id"
                    rules={{
                      required: shouldGrantBadge
                        ? "付与するバッジを選択してください。"
                        : false,
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        aria-label="バッジタイプの選択"
                        className="h-[50px] w-full [&_.ant-select-selection-item]:!flex [&_.ant-select-selection-item]:!items-center [&_.ant-select-selector]:!h-[50px] [&_.ant-select-selector]:!rounded-[6px] [&_.ant-select-selector]:!border-[#d5d5d5] [&_.ant-select-selector]:!px-[15px]"
                        disabled={isBadgeLoading || badgeOptions.length === 0}
                        loading={isBadgeLoading}
                        notFoundContent="バッジデータがありません"
                        options={badgeOptions.map((option) => ({
                          value: option.value,
                          label: (
                            <span className="flex min-w-0 items-center gap-[10px]">
                              {option.iconUrl ? (
                                <img
                                  alt=""
                                  aria-hidden="true"
                                  className="h-8 w-8 shrink-0 object-contain"
                                  src={option.iconUrl}
                                />
                              ) : null}
                              <span className="truncate">{option.label}</span>
                            </span>
                          ),
                        }))}
                        placeholder={
                          isBadgeLoading
                            ? "バッジデータを読み込み中です"
                            : "付与するバッジを選択してください"
                        }
                        suffixIcon={
                          <IconQuestKeyboardArrowDown className="h-5 w-5" />
                        }
                        value={field.value || undefined}
                      />
                    )}
                  />
                  <FieldError message={errors.reward_badge_id?.message} />
                </QuestFormField>
                {badgeErrorMessage ? (
                  <p className="text-[13px] leading-[1.6] text-[#de3c3f]">
                    {badgeErrorMessage}
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-5 border-t border-[#d5d5d5] pt-5 sm:grid-cols-2 sm:gap-[15px]">
              <QuestFormField label="開始月">
                <Controller
                  control={control}
                  name="active_start_month"
                  render={({ field }) => (
                    <MonthField
                      {...field}
                      disabled={isDefaultHiddenQuest || isLoginStreak}
                      placeholder={
                        isDefaultHiddenQuest || isLoginStreak
                          ? "常時開催"
                          : "開始月を選択"
                      }
                    />
                  )}
                />
                <FieldError message={errors.active_start_month?.message} />
              </QuestFormField>

              <QuestFormField label="終了月">
                <Controller
                  control={control}
                  name="active_end_month"
                  rules={{
                    validate: (value) =>
                      isDefaultHiddenQuest ||
                      isLoginStreak ||
                      !activeStartMonth ||
                      !value ||
                      value >= activeStartMonth ||
                      "終了月は開始月以降を選択してください。",
                  }}
                  render={({ field }) => (
                    <MonthField
                      {...field}
                      disabled={isDefaultHiddenQuest || isLoginStreak}
                      placeholder={
                        isDefaultHiddenQuest || isLoginStreak
                          ? "常時開催"
                          : "終了月を選択"
                      }
                    />
                  )}
                />
                <FieldError message={errors.active_end_month?.message} />
              </QuestFormField>
            </div>

            <QuestFormField label="達成条件の詳細 / 条件・ルール">
              <textarea
                className="h-[200px] w-full resize-none rounded-[6px] border border-[#d5d5d5] bg-white px-[15px] py-[15px] text-[16px] leading-[1.6] text-black outline-none placeholder:text-[#cccccc] focus:border-[#1b4b86]"
                maxLength={150}
                placeholder="クエスト達成に必要な具体的なルールや制限事項を記載してください。"
                {...register("description")}
              />
            </QuestFormField>

            <div className="flex items-center justify-between border-t border-[#d5d5d5] pt-[20px]">
              <FieldLabel>状態：有効 / 無効</FieldLabel>
              <label className="relative inline-flex h-[28px] w-[52px] cursor-pointer items-center">
                <input
                  className="peer sr-only"
                  type="checkbox"
                  {...register("is_active")}
                />
                <span className="absolute inset-0 rounded-full bg-[#d5d5d5] transition-colors peer-checked:bg-[#1b4b86]" />
                <span className="relative ml-[3px] h-[22px] w-[22px] rounded-full bg-white transition-transform peer-checked:translate-x-[24px]" />
              </label>
            </div>
          </div>

          {createErrorMessage ? (
            <p className="mt-[20px] whitespace-pre-line text-[13px] leading-[1.6] text-[#de3c3f]">
              {createErrorMessage}
            </p>
          ) : null}

          <div className="mt-10 flex flex-col-reverse gap-2.5 px-3 sm:grid sm:grid-cols-2 sm:gap-[15px] sm:px-0">
            <button
              className="flex h-[50px] items-center justify-center rounded-[6px] bg-[#eef5fa] text-[18px] font-bold leading-[1.6] text-[#1b4b86]"
              disabled={isCreating}
              onClick={handleRequestCancel}
              type="button"
            >
              キャンセル
            </button>
            <button
              className="flex h-[50px] items-center justify-center rounded-[6px] bg-[#1b4b86] text-[18px] font-bold leading-[1.6] text-white disabled:opacity-50"
              disabled={
                isCreating ||
                (shouldGrantBadge &&
                  (isBadgeLoading || badgeOptions.length === 0))
              }
              type="submit"
            >
              {isCreating ? "保存中..." : "保存する"}
            </button>
          </div>
        </form>
      </aside>

      <UnsavedChangesModal
        isOpen={isUnsavedConfirmOpen}
        onCancel={handleStayOnForm}
        onConfirm={handleConfirmCancel}
      />
    </div>
  );
};

export default CreateQuestDrawer;
